import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  LayoutGrid, 
  Mic2, 
  Zap, 
  MoreVertical, 
  Settings, 
  ExternalLink,
  Users,
  Loader2,
  X,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { getProjects, getProjectStats, createProject, uploadProjectLogo, deleteProject } from '../../lib/apiClient';
import { useAuth } from '../../hooks/useAuth';

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const { user } = useAuth();

  async function loadProjects() {
      try {
          // setLoading(true); // Don't flicker on reload
          const res = await getProjects();
          const projectList = res?.data || [];
          
          const enhanced = await Promise.all(projectList.map(async (p) => {
              try {
                  const statsRes = await getProjectStats(p.id);
                  return { ...p, stats: statsRes?.data || { episodeCount: 0, insightCount: 0, topicCount: 0 } };
              } catch (e) {
                  return { ...p, stats: { episodeCount: 0, insightCount: 0, topicCount: 0 } };
              }
          }));
          
          setProjects(enhanced);
      } catch (e) {
          console.error('Failed to load projects', e);
      } finally {
          setLoading(false);
      }
  }

  useEffect(() => {
      loadProjects();
  }, []);

  const handleCreateSuccess = () => {
      setIsCreateModalOpen(false);
      loadProjects();
  };

  const handleDeleteSuccess = () => {
    setProjectToDelete(null);
    loadProjects();
  };

  if (loading) return <div className="h-screen flex items-center justify-center bg-background text-white"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-background text-white font-sans">
      
      {/* Header */}
      <header className="border-b border-white/10 bg-surface/50 sticky top-0 z-10 backdrop-blur-md">
        <div className="container mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center font-bold text-white">
              P
            </div>
            <span className="font-bold text-lg">PodVault</span>
            <div className="h-4 w-px bg-white/10 mx-2" />
            <nav className="flex items-center gap-6 text-sm font-medium">
              <span className="text-white">Projects</span>
              <span className="text-slate-500 hover:text-white cursor-pointer transition-colors">Docs</span>
              <span className="text-slate-500 hover:text-white cursor-pointer transition-colors">Support</span>
            </nav>
          </div>
          
          <div className="flex items-center gap-4">
             <div className="flex items-center gap-2 text-sm text-slate-400">
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold text-white border border-white/5">
                  {user?.email?.[0].toUpperCase() || 'U'}
                </div>
             </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-6xl px-6 py-12">
        
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-1">Projects & Brands</h1>
            <p className="text-slate-400">Manage your podcast portfolio and authority engines.</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          
          {/* Project Cards */}
          {projects.map(project => (
            <div key={project.id} className="bg-surface border border-white/5 rounded-2xl p-6 hover:border-white/20 transition-all group flex flex-col h-full relative">
              
              {/* Card Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center font-bold text-xl text-white shadow-lg overflow-hidden`}>
                    {project.logo_url ? <img src={project.logo_url} alt={project.name} className="w-full h-full object-cover" /> : project.name.substring(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-bold text-white text-lg">{project.name}</h3>
                    <span className="text-xs bg-white/10 px-2 py-0.5 rounded text-slate-300 font-medium">
                      {project.role || 'Member'}
                    </span>
                  </div>
                </div>
                <button 
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setProjectToDelete(project);
                    }}
                    className="text-slate-500 hover:text-red-400 transition-colors p-1"
                    title="Delete Brand"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-2 mb-6">
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-white mb-1">{project.stats.episodeCount}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">Episodes</div>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-white mb-1">{project.stats.insightCount}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">Insights</div>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-white mb-1">{project.stats.topicCount}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">Topics</div>
                </div>
              </div>

              {/* Shows List (Mini) - Currently mocking shows as project = show conceptually */}
              <div className="mb-6 space-y-2 flex-grow">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Details</h4>
                <p className="text-sm text-slate-400 line-clamp-2">{project.description || 'No description'}</p>
              </div>

              {/* Actions */}
              <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/5">
                <Link to={`/app/brand/${project.slug}/dashboard`} className="col-span-2 py-2 bg-primary/10 text-primary hover:bg-primary hover:text-white font-bold rounded-lg text-sm text-center transition-colors">
                  Open Dashboard
                </Link>
                <Link to={`/app/brand/${project.slug}/settings`} className="py-2 border border-white/10 text-slate-400 hover:text-white hover:border-white/30 font-medium rounded-lg text-sm text-center transition-colors flex items-center justify-center gap-2">
                  <Settings className="w-4 h-4" /> Settings
                </Link>
                <a href={`${process.env.REACT_APP_PUBLIC_URL || 'http://localhost:3000'}/brands/${project.slug}`} target="_blank" rel="noopener noreferrer" className="py-2 border border-white/10 text-slate-400 hover:text-white hover:border-white/30 font-medium rounded-lg text-sm text-center transition-colors flex items-center justify-center gap-2">
                  <ExternalLink className="w-4 h-4" /> Public Page
                </a>
              </div>
            </div>
          ))}

          {/* Add New Project Card */}
          <button 
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-surface/30 border-2 border-dashed border-white/10 rounded-2xl p-6 hover:border-primary/50 hover:bg-surface/50 transition-all flex flex-col items-center justify-center gap-4 text-center h-full min-h-[300px] group"
          >
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center group-hover:scale-110 transition-transform group-hover:bg-primary/20">
              <Plus className="w-8 h-8 text-slate-400 group-hover:text-primary transition-colors" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white group-hover:text-primary transition-colors">Create New Brand</h3>
              <p className="text-slate-400 text-sm mt-2 max-w-[200px] mx-auto">
                Start a new authority engine for your brand.
              </p>
            </div>
          </button>

        </div>
      </main>

      {/* Create Project Modal */}
      {isCreateModalOpen && (
        <CreateProjectModal onClose={() => setIsCreateModalOpen(false)} onSuccess={handleCreateSuccess} />
      )}

      {/* Delete Project Modal */}
      {projectToDelete && (
        <DeleteProjectModal 
            project={projectToDelete} 
            onClose={() => setProjectToDelete(null)} 
            onSuccess={handleDeleteSuccess} 
        />
      )}
    </div>
  );
}

function CreateProjectModal({ onClose, onSuccess }) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
    const [websiteUrl, setWebsiteUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!logoFile) {
            setLogoPreviewUrl('');
            return;
        }
        const objectUrl = URL.createObjectURL(logoFile);
        setLogoPreviewUrl(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [logoFile]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const res = await createProject({ name, description, logoUrl, websiteUrl });
            const createdProject = res?.data || null;
            if (res && (res.success || createdProject)) {
                if (logoFile && createdProject?.id) {
                    await uploadProjectLogo(createdProject.id, logoFile);
                }
                onSuccess();
            } else {
                throw new Error(res?.error || 'Failed to create project');
            }
        } catch (err) {
            console.error(err);
            setError(err.message || 'An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-surface border border-white/10 rounded-2xl p-6 max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-200">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-white">Create New Brand</h3>
                    <button onClick={onClose} className="text-slate-500 hover:text-white">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Brand Name</label>
                        <input 
                            type="text" 
                            required 
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full bg-black/20 border border-white/10 rounded-lg p-3 text-white focus:ring-1 focus:ring-primary outline-none transition-all"
                            placeholder="My Podcast Brand"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Logo URL (Optional)</label>
                        <input 
                            type="url" 
                            value={logoUrl}
                            onChange={(e) => setLogoUrl(e.target.value)}
                            className="w-full bg-black/20 border border-white/10 rounded-lg p-3 text-white focus:ring-1 focus:ring-primary outline-none transition-all"
                            placeholder="https://example.com/logo.png"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Logo File (Optional)</label>
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center overflow-hidden text-white font-bold text-lg">
                                {logoPreviewUrl ? (
                                    <img src={logoPreviewUrl} alt="Logo preview" className="w-full h-full object-cover" />
                                ) : (
                                    <span>{name ? name.substring(0, 2).toUpperCase() : 'PV'}</span>
                                )}
                            </div>
                            <label className={`px-3 py-2 rounded-lg border border-white/10 text-xs text-slate-200 hover:bg-white/5 ${loading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                                Choose File
                                <input
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    disabled={loading}
                                    className="hidden"
                                    onChange={(e) => {
                                        const file = e.target.files && e.target.files[0];
                                        e.target.value = '';
                                        if (file) setLogoFile(file);
                                    }}
                                />
                            </label>
                            {logoFile && (
                                <button
                                    type="button"
                                    disabled={loading}
                                    className="px-3 py-2 rounded-lg border border-white/10 text-xs text-slate-400 hover:text-white hover:bg-white/5"
                                    onClick={() => setLogoFile(null)}
                                >
                                    Remove
                                </button>
                            )}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-2 leading-relaxed">
                            Recommended: square image, 512×512. Allowed: PNG/JPG/WebP. Max 2MB.
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Website URL (Optional)</label>
                        <input 
                            type="url" 
                            value={websiteUrl}
                            onChange={(e) => setWebsiteUrl(e.target.value)}
                            className="w-full bg-black/20 border border-white/10 rounded-lg p-3 text-white focus:ring-1 focus:ring-primary outline-none transition-all"
                            placeholder="https://yourbrand.com"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Description (Optional)</label>
                        <textarea 
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="w-full bg-black/20 border border-white/10 rounded-lg p-3 text-white focus:ring-1 focus:ring-primary outline-none transition-all h-24 resize-none"
                            placeholder="What is this brand about?"
                        />
                    </div>

                    {error && (
                        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm text-center">
                            {error}
                        </div>
                    )}
                    
                    <button type="submit" disabled={loading} className="w-full py-3 bg-primary hover:bg-primary-hover text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Brand'}
                    </button>
                </form>
            </div>
        </div>
    );
}

function DeleteProjectModal({ project, onClose, onSuccess }) {
    const [confirmName, setConfirmName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (confirmName !== project.name) {
            setError('Brand name does not match.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const res = await deleteProject(project.id);
            if (res && (res.success || res.message)) {
                onSuccess();
            } else {
                throw new Error(res?.error || 'Failed to delete project');
            }
        } catch (err) {
            console.error(err);
            setError(err.message || 'An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-surface border border-white/10 rounded-2xl p-6 max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-200">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <AlertTriangle className="w-6 h-6 text-red-500" />
                        Delete Brand?
                    </h3>
                    <button onClick={onClose} className="text-slate-500 hover:text-white">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                
                <div className="mb-6">
                    <p className="text-slate-300 text-sm mb-4">
                        Are you sure you want to delete <span className="font-bold text-white">{project.name}</span>?
                    </p>
                    <p className="text-red-400 text-xs bg-red-500/10 p-3 rounded-lg border border-red-500/20">
                        <strong className="block mb-1">Warning: This action cannot be undone.</strong>
                        All episodes, insights, and data associated with this brand will be permanently deleted.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-slate-400 mb-1">
                            Type <span className="text-white select-all">{project.name}</span> to confirm
                        </label>
                        <input 
                            type="text" 
                            required 
                            value={confirmName}
                            onChange={(e) => setConfirmName(e.target.value)}
                            className="w-full bg-black/20 border border-white/10 rounded-lg p-3 text-white focus:ring-1 focus:ring-red-500 outline-none transition-all"
                            placeholder={project.name}
                        />
                    </div>

                    {error && (
                        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm text-center">
                            {error}
                        </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-3">
                        <button 
                            type="button" 
                            onClick={onClose}
                            className="py-3 bg-white/5 hover:bg-white/10 text-slate-300 font-bold rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            disabled={loading || confirmName !== project.name} 
                            className="py-3 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete Brand'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
